/**
 * RuptureSkillEffect — Earth tree stone_fist path active (rupture).
 *
 * Phased skill: wind-up (500ms) → active (spike line erupts) → recovery → cooldown (long).
 *
 * On hit:
 * - Line AoE of `lineLengthTiles` tiles in the player's facing direction (captured at wind-up begin)
 * - All enemies on any tile of the line take 18 damage and receive stun for 500ms
 * - If Shatterpoint is unlocked: enemies also receive `vulnerable` debuff (30% more incoming damage, 3s)
 * - If Grooved is unlocked: after spikes recede, creates a line-shaped rough terrain zone along the spike path
 *
 * Facing direction is locked at wind-up begin (onActivate), not at hit resolution.
 * Extended Fault: queries `lineLengthTiles` additive key; base 4 tiles, +2 after unlock.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Stone Fist Fork 2 — Rupture Sub-Path
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface RuptureDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly player: {
    /** Returns the player's current facing angle in degrees (0 = east, 90 = south, etc.). */
    getFacingAngle(): number;
  };
  readonly targeting: {
    /** Returns enemy IDs whose bounding box intersects any tile on the spike line. */
    getLineTargets(facingAngleDeg: number, lengthTiles: number): string[];
  };
  readonly combat: {
    applyDamage(
      targetId: string,
      damage: number,
      opts: { sourceSkillId: string },
    ): void;
  };
  readonly statusEffects: {
    apply(effectId: string, ...args: unknown[]): void;
  };
  readonly zones: {
    create(config: {
      type: string;
      shape: string;
      facingAngle: number;
      lengthTiles: number;
      widthTiles: number;
      durationMs: number;
      speedMultiplier: number;
      sourceSkillId: string;
    }): string;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface RuptureConfig {
  readonly windupMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  /** Base damage per hit. */
  readonly baseDamage: number;
  /** Base line length in tiles before resolver additions. */
  readonly baseLineLengthTiles: number;
  /** Stun duration on landing. */
  readonly stunDurationMs: number;
  /** Grooved: rough terrain speed multiplier. */
  readonly groovedSpeedMultiplier: number;
}

const DEFAULT_CONFIG: RuptureConfig = {
  windupMs: 500,
  recoveryMs: 150,
  cooldownMs: 2000,        // long cooldown tier
  baseDamage: 18,
  baseLineLengthTiles: 4,
  stunDurationMs: 500,
  groovedSpeedMultiplier: 0.75,
};

// ---------------------------------------------------------------------------
// Phase sequence
// ---------------------------------------------------------------------------

const PHASE_SEQUENCE: readonly SkillPhase[] = ['windup', 'active', 'recovery', 'cooldown'];

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class RuptureSkillEffect extends PhasedSkillEffect {
  readonly skillId = 'rupture';

  protected readonly phaseSequence = PHASE_SEQUENCE;

  private readonly deps: RuptureDeps;
  private readonly config: RuptureConfig;

  // Per-activation state
  private _facingAngleDeg = 0;   // captured at wind-up begin
  private _targetsHit: string[] = [];
  private _lastDamageDealt = 0;
  private _lastLineLengthTiles = 0;
  private _groovedZoneCreated = false;
  private _activationCount = 0;

  constructor(deps: RuptureDeps, config: Partial<RuptureConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  /** Facing direction captured at wind-up begin. */
  get facingAngleDeg(): number { return this._facingAngleDeg; }
  get targetsHit(): readonly string[] { return this._targetsHit; }
  get lastDamageDealt(): number { return this._lastDamageDealt; }
  get lastLineLengthTiles(): number { return this._lastLineLengthTiles; }
  get groovedZoneCreated(): boolean { return this._groovedZoneCreated; }
  get activationCount(): number { return this._activationCount; }

  // --- PhasedSkillEffect overrides ---

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return 16;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected override onActivate(): void {
    // Reset per-activation state
    this._targetsHit = [];
    this._lastDamageDealt = 0;
    this._lastLineLengthTiles = 0;
    this._groovedZoneCreated = false;
    this._activationCount++;

    // Lock facing direction at wind-up begin — not at hit resolution
    this._facingAngleDeg = this.deps.player.getFacingAngle();
  }

  protected override onPhaseEnter(phase: SkillPhase): void {
    if (phase !== 'active') return;

    // Resolve line length (Extended Fault adds 2)
    const lineLengthDelta = this.deps.resolver.getAdditiveEffect('rupture', 'lineLengthTiles');
    const lineLengthTiles = this.config.baseLineLengthTiles + lineLengthDelta;
    this._lastLineLengthTiles = lineLengthTiles;

    // Get targets on spike line using the locked facing direction
    const targets = this.deps.targeting.getLineTargets(this._facingAngleDeg, lineLengthTiles);

    // Apply damage and stun to each target
    for (const targetId of targets) {
      this.deps.combat.applyDamage(targetId, this.config.baseDamage, {
        sourceSkillId: 'rupture',
      });

      this.deps.statusEffects.apply('stun', this.config.stunDurationMs, {
        sourceSkillId: 'rupture',
        targetId,
      });

      // Shatterpoint: apply vulnerability debuff on each launched enemy
      if (this.deps.resolver.hasEffect('rupture', 'vulnerabilityBonus')) {
        const vulnerabilityBonus =
          this.deps.resolver.getAdditiveEffect('rupture', 'vulnerabilityBonus');
        const vulnerabilityDurationMs =
          this.deps.resolver.getAdditiveEffect('rupture', 'vulnerabilityDurationMs');

        this.deps.statusEffects.apply('vulnerability', {
          durationMs: vulnerabilityDurationMs,
          intensity: vulnerabilityBonus,
          sourceSkillId: 'rupture',
          targetId,
        });
      }
    }

    this._targetsHit = [...targets];
    this._lastDamageDealt = this.config.baseDamage * targets.length;

    // Grooved: create line-shaped rough terrain zone after spikes
    if (this.deps.resolver.hasEffect('rupture', 'roughTerrainDurationMs')) {
      const roughTerrainDurationMs =
        this.deps.resolver.getAdditiveEffect('rupture', 'roughTerrainDurationMs');

      this.deps.zones.create({
        type: 'rough_terrain',
        shape: 'line',
        facingAngle: this._facingAngleDeg,
        lengthTiles: lineLengthTiles,
        widthTiles: 1,
        durationMs: roughTerrainDurationMs,
        speedMultiplier: this.config.groovedSpeedMultiplier,
        sourceSkillId: 'rupture',
      });
      this._groovedZoneCreated = true;
    }

    this.markHitApplied();
  }
}
